import { useCanGoBack, useRouter } from '@tanstack/react-router';
import classNames from 'classnames';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import type { FileRouteTypes } from '@/routeTree.gen';
import { Flex } from '../Flex';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { TextButton } from '../TextButton';
import type { SharedComponentProps } from '../types';
import * as styles from './index.css';

export type PageProps = SharedComponentProps<'div'>;

function Root({ children, className }: PageProps) {
    return <div className={classNames(styles.page, className)}>{children}</div>;
}

export type PageHeaderProps = SharedComponentProps<'h1'> & {
    title: string;
    children?: ReactNode;
};

function Header({ title, children, className }: PageHeaderProps) {
    return (
        <Flex as="header" gap="2" align="center" className={classNames(styles.header, className)}>
            <h1 className={styles.title}>{title}</h1>
            {children}
        </Flex>
    );
}

export type BackLinkProps = React.ComponentProps<'div'> & {
    to: FileRouteTypes['to'];
    label: string;
    forceNavigate?: boolean;
};

export function BackLink({ to, label, forceNavigate, ...props }: BackLinkProps) {
    const router = useRouter();
    const canGoBack = useCanGoBack();

    return (
        <div {...props}>
            {canGoBack && !forceNavigate ? (
                <TextButton onClick={() => router.history.back()}>
                    <Icon icon={ArrowLeft} /> {label}
                </TextButton>
            ) : (
                <Link noUnderline to={to}>
                    <Icon icon={ArrowLeft} /> {label}
                </Link>
            )}
        </div>
    );
}

export type PageContentProps = SharedComponentProps<'div'> & {
    gap?: React.ComponentProps<typeof Flex>['gap'];
    children: ReactNode;
};

function Content({ gap = '6', children, className, ...props }: PageContentProps) {
    return (
        <Flex direction="column" gap={gap} className={classNames(className)} {...props}>
            {children}
        </Flex>
    );
}

export const Page = { Root, Header, BackLink, Content };
